
import Vue from 'vue';
import PaginatedTable from '../PaginatedTable.vue';
import WorkerCompetenceCheckModal from './WorkerCompetenceCheckModal.vue';
import axios, { AxiosResponse } from "axios";
import { EventBus } from '@/plugins/eventBus';

export default Vue.extend({
  name: 'WorkerCompetenceCheck',
  components: { PaginatedTable, WorkerCompetenceCheckModal },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      items: [] as any[],
      lastPage: 1,
      loading: false,
      showModal: false,
      selectedCompetenceCheck: {},
      isCreateMode: false,
      showDeleteDialog: false,
      deleting: false,
      competenceCheckToDelete: null as number | null,
      headers: [
        { text: this.$vuetify.lang.t('$vuetify.company'), value: 'companyName', sortable: false, width: '200px', fixedWidth: true },
        { text: this.$vuetify.lang.t('$vuetify.qualification'), value: 'qualification', sortable: false, width: '200px', fixedWidth: true },
        { text: this.$vuetify.lang.t('$vuetify.is_take_back'), value: 'isTakeBack', align: 'center', sortable: false, width: '100px', fixedWidth: true },
        { text: this.$vuetify.lang.t('$vuetify.exchange'), value: 'exchange', sortable: false, width: '200px', fixedWidth: true },
        { text: this.$vuetify.lang.t('$vuetify.period'), value: 'period', sortable: false, width: '250px', align: 'center', fixedWidth: true },
        { text: this.$vuetify.lang.t('$vuetify.date_call'), value: 'dateCall', sortable: false, width: '150px', fixedWidth: true },
        { text: this.$vuetify.lang.t('$vuetify.action'), value: 'action', sortable: false, align: 'center', width: '80px', fixedWidth: true },
      ],
    };
  },
  methods: {
    async fetchCompetenceChecks(page = 1, options = {}) {
      this.loading = true;
      try {
        const url = `/v1/workers/${this.id}/competence_check`;
        const response: AxiosResponse = await axios.get(url);
        
        let rawItems = [];
        let lastPageValue = 1;        
        if (Array.isArray(response.data)) {
          rawItems = response.data;
        } else if (response.data && 'items' in response.data) {
          rawItems = Array.isArray(response.data.items) ? response.data.items : [];
          if (response.data.lastPage !== undefined && response.data.lastPage !== null) {
            lastPageValue = response.data.lastPage;
          }
        } else {
          rawItems = [];
        }
        
        // Filter out null and undefined items
        const validItems = rawItems.filter((item: any) => item != null);
        
        this.items = validItems.map((item: any) => ({
          ...item,
          isTakeBack: item.is_take_back ? '✅' : '❌',
          period: this.formatPeriod(item.date_from, item.date_to),
          dateCall: this.formatDateTime(item.date_call),
          action: '👁️',
          companyName: this.getCompanyName(item.company, item.registrate_company_name),
          contract_type: this.formatContractType(item.contract_type)
        }));
        
        this.lastPage = lastPageValue;
        
      } catch (error) {
        this.items = [];
        this.lastPage = 0;
        EventBus.$emit('snackbar', { axiosError: error });
      } finally {
        this.loading = false;
      }
    },
    getCompanyName(company: any, registrateCompany: any): string {
      if (company && company !== '' && company !== null && company !== undefined) {
        return company;
      }
      if (registrateCompany && registrateCompany !== '' && registrateCompany !== null && registrateCompany !== undefined) {
        return registrateCompany;
      }
      return '';
    },
    formatContractType(contractType: string) {
      // this switch format v1 values into v3 ones
      switch (contractType) {
        case 'Stage':
          return 'internship'
        case 'Intérim':
          return 'temporary'
        case 'CDI':
          return 'permanent contract'
        case 'CDD':
          return 'fixed term contract'
        case 'Alternance':
          return 'apprenticeship'
        default:
          return contractType
      }
    },
    formatPeriod(dateFrom: any, dateTo: any): string {
      if (!dateFrom && !dateTo) return '';
      
      const formatDate = (date: any) => {
        if (!date) return '';
        // Parse date in YYYY-MM-DD format correctly
        const parts = date.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1; // Month is 0-indexed
          const day = parseInt(parts[2]);
          const d = new Date(year, month, day);
          if (isNaN(d.getTime()) || !isFinite(d.getTime())) return '';
          return d.toLocaleDateString('fr-FR');
        }
        // Fallback to original method for other formats
        const d = new Date(date);
        if (isNaN(d.getTime()) || !isFinite(d.getTime())) return '';
        return d.toLocaleDateString('fr-FR');
      };
      
      const fromStr = formatDate(dateFrom);
      const toStr = formatDate(dateTo);
      
      if (fromStr && toStr) return `du ${fromStr} au ${toStr}`;
      if (fromStr) return `du ${fromStr}`;
      if (toStr) return `au ${toStr}`;
      
      return '';
    },
    formatDateTime(dateTime: any): string {
      if (!dateTime) return '';
      
      const d = new Date(dateTime);
      if (isNaN(d.getTime()) || !isFinite(d.getTime())) return '';
      
      return d.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    openModal(item: any) {
      this.selectedCompetenceCheck = item;
      this.isCreateMode = false;
      this.showModal = true;
    },
    openCreateModal() {
      this.selectedCompetenceCheck = {};
      this.isCreateMode = true;
      this.showModal = true;
    },
    onCompetenceCheckCreated() {
      // Refresh list after creation
      this.fetchCompetenceChecks();
    },
    onCompetenceCheckUpdated() {
      // Refresh list after update
      this.fetchCompetenceChecks();
    },
    confirmDelete(item: any) {
      this.competenceCheckToDelete = item.id;
      this.showDeleteDialog = true;
    },
    cancelDelete() {
      this.showDeleteDialog = false;
      this.competenceCheckToDelete = null;
    },
    async deleteCompetenceCheck() {
      if (!this.competenceCheckToDelete) return;
      
      this.deleting = true;
      try {
        await axios.delete(`/v1/workers/${this.id}/competence_check/${this.competenceCheckToDelete}`);
        
        EventBus.$emit('snackbar', {
          message: this.$vuetify.lang.t('$vuetify.competence_check_deleted_successfully'),
          color: 'success'
        });
        
        // Close dialog and reset state
        this.showDeleteDialog = false;
        this.competenceCheckToDelete = null;
        
        // Refresh the list after deletion
        this.fetchCompetenceChecks();
      } catch (error) {
        EventBus.$emit('snackbar', { axiosError: error });
      } finally {
        this.deleting = false;
      }
    }
  },
  created() {
    this.fetchCompetenceChecks();
  },
  watch: {
    id(newVal: any, oldVal: any) {
      if (newVal !== oldVal) this.fetchCompetenceChecks();
    }
  }
});
